import { Directive, computed, inject, input } from '@angular/core';
import { PlanningDataSource, PlanningItemDto } from '@digiwall/goliath-documents/proxy';
import {
  PlanningCardContext,
  PlanningCardFieldVisibilityStore,
} from '../card-config/planning-card-field-visibility.store';
import { itemKanbanId } from '../planning-board.mapping';

/**
 * Shared, business-agnostic plumbing for a planning item card. Every data source's card is rendered by
 * the board through NgComponentOutlet with only the generic {@link PlanningItemDto}; this base holds the
 * bits that don't vary per source — the dto input, the typed `displayFields` view, the planned/backlog
 * context, and the per-field visibility lookup. A concrete card extends it with its own `TFields` type
 * and `dataSource`, then renders (and gates) its own fields. Purely structural — it carries no business
 * data (status/kind/schedule computeds stay in the concrete card).
 */
@Directive()
export abstract class BasePlanningCardComponent<TFields> {
  private readonly visibility = inject(PlanningCardFieldVisibilityStore);

  /** This card's data source — selects which visibility bucket applies. */
  protected abstract readonly dataSource: PlanningDataSource;

  protected readonly dto = input.required<PlanningItemDto>();

  /**
   * Precomputed by the board shell: is the item's desired date critical for its current placement?
   * Drives the red (critical) vs grey styling of the "Souhaité" line. Passed as a card input because
   * criticality depends on board state (granularity, column, today) the card doesn't otherwise know.
   */
  protected readonly requestedDateCritical = input(false);

  /** Typed view of the loosely-typed `displayFields` payload. */
  protected readonly fields = computed(() => (this.dto().displayFields ?? {}) as TFields);

  /** Planned items carry a plannedDate; backlog items don't — the two contexts toggle independently. */
  protected readonly context = computed<PlanningCardContext>(() =>
    this.dto().plannedDate ? 'planned' : 'backlog',
  );

  /** Kanban item id (namespaced by data source) — matches the id the board tracks, for status edits. */
  protected readonly statusItemId = computed(() => itemKanbanId(this.dataSource, this.dto().id!));

  /** Status is editable only for planned cards — backlog cards keep their status read-only. */
  protected readonly statusEditable = computed(() => this.context() === 'planned');

  /** Whether a toggleable body field is currently shown for this card's source + context. */
  protected isVisible(fieldKey: string): boolean {
    return this.visibility.isVisible(this.dataSource, this.context(), fieldKey);
  }
}
